import { useState } from 'react'

interface Segment {
  label: string
  grams: number
  cals: number
  color: string
}

interface Props {
  protein: number
  proteinCals: number
  carbs: number
  carbsCals: number
  fats: number
  fatsCals: number
}

function MacroChart({ protein, proteinCals, carbs, carbsCals, fats, fatsCals }: Props) {
  const [active, setActive] = useState<number | null>(null)

  const segments: Segment[] = [
    { label: 'Protein', grams: protein, cals: proteinCals, color: '#3b82f6' },
    { label: 'Carbs', grams: carbs, cals: carbsCals, color: '#22d3ee' },
    { label: 'Fats', grams: fats, cals: fatsCals, color: '#818cf8' },
  ]

  const totalCals = Math.max(1, proteinCals + carbsCals + fatsCals)
  const radius = 70
  const circumference = 2 * Math.PI * radius
  let offset = 0

  return (
    <div className="flex flex-col sm:flex-row items-center gap-7">
      <div className="relative shrink-0">
        <svg width="180" height="180" viewBox="0 0 180 180" className="-rotate-90">
          <circle cx="90" cy="90" r={radius} fill="none" stroke="#1c2230" strokeWidth="18" />
          {segments.map((seg, i) => {
            const pct = seg.cals / totalCals
            const dash = pct * circumference
            const circle = (
              <circle
                key={seg.label}
                cx="90"
                cy="90"
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth={active === i ? 22 : 18}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                className="transition-all duration-300 cursor-pointer"
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                style={{ opacity: active === null || active === i ? 1 : 0.35 }}
              />
            )
            offset += dash
            return circle
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          {active === null ? (
            <>
              <span className="font-mono text-2xl font-bold text-white">
                {totalCals.toLocaleString()}
              </span>
              <span className="text-[11px] uppercase tracking-wide text-slate-500">kcal split</span>
            </>
          ) : (
            <>
              <span className="font-mono text-2xl font-bold text-white">
                {segments[active].grams}g
              </span>
              <span className="text-[11px] uppercase tracking-wide text-slate-500">
                {segments[active].label}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="w-full space-y-3">
        {segments.map((seg, i) => {
          const pct = Math.round((seg.cals / totalCals) * 100)
          return (
            <button
              key={seg.label}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              className={`w-full text-left rounded-xl border px-4 py-3 transition-all ${
                active === i
                  ? 'border-white/20 bg-white/[0.06]'
                  : 'border-white/[0.06] bg-white/[0.02]'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2.5 text-[14px] font-medium text-white">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: seg.color }}
                    aria-hidden="true"
                  />
                  {seg.label}
                </span>
                <span className="font-mono text-[14px] text-white">{seg.grams}g</span>
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[12px] text-slate-500">
                <span>{seg.cals.toLocaleString()} kcal</span>
                <span>{pct}%</span>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default MacroChart
